import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  contractorFormSchema,
  ContractorAgreementFormData,
  GenerateContractorAgreementResponse,
} from "@/types/contractor-agreement";

// Import shared layout components
import PublicNavBar from "@/components/layout/PublicNavBar";
import PublicFooter from "@/components/layout/PublicFooter";

// Import family image
import wholeFamily from "@/images/wholeFamily.webp";

interface ContractorAgreementFormProps {
  onSuccess?: () => void;
  embedded?: boolean;
}

export default function ContractorAgreementForm({
  onSuccess,
  embedded = false,
}: ContractorAgreementFormProps = {}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContractorAgreementFormData>({
    resolver: zodResolver(contractorFormSchema),
    defaultValues: {
      // Parties
      client_full_legal_name: "",
      client_address: "",
      contractor_full_legal_name: "",
      contractor_address: "",
      contractor_business_name: "",

      // Services
      schedule_a_services_description: "",

      // Fees & Invoicing
      fee_currency_symbol: "£",
      fee_rate: undefined as unknown as number,
      fee_basis: "hour",
      invoice_frequency: "monthly",
      payment_due_days: 30,
      schedule_b_fees_description: "",

      // Safeguarding & Background Checks
      background_check_examples: "DBS certificate",
      schedule_c_safeguarding_description: "",

      // Confidentiality & Data Protection
      data_protection_regulation: "UK GDPR",

      // Insurance & Liability
      has_insurance: false,
      insurance_currency_symbol: "£",
      insurance_cover_amount: undefined as unknown as number,
      liability_cap_currency_symbol: "£",
      liability_cap_amount: undefined as unknown as number,

      // Term & Termination
      start_date: "",
      end_date_or_completion: "",
      termination_notice_days: 30,

      // Non-Solicitation (Optional)
      non_solicitation_clause: false,
      non_solicitation_months: 6,

      // Governing Law & Dispute Resolution
      governing_law_country: "United Kingdom",
      mediation_rules: "CEDR",

      // User contact information
      userFirstName: "",
      userLastName: "",
      userEmail: "",
    },
  });

  async function onSubmit(data: ContractorAgreementFormData) {
    try {
      setIsSubmitting(true);

      if (embedded) {
        // When embedded, we're authenticated and don't need user contact info
        const response =
          await ApiService.request<GenerateContractorAgreementResponse>(
            EndpointKeys.GENERATE_CONTRACTOR_AGREEMENT,
            {
              method: "POST",
              body: {
                agreementData: data,
              },
              skipAuth: false, // Use authentication when embedded
            },
          );

        if (response.status === "success" && response.agreement_id) {
          toast({
            title: "Success!",
            description:
              "Your contractor agreement has been generated successfully.",
          });

          form.reset(); // Clear the form on success
          if (onSuccess) {
            onSuccess();
          }
        } else {
          throw new Error(
            response.error || "Failed to generate contractor agreement",
          );
        }
      } else {
        // Public form flow - extract user contact info
        const { userEmail, userFirstName, userLastName, ...agreementData } =
          data;

        const response =
          await ApiService.request<GenerateContractorAgreementResponse>(
            EndpointKeys.GENERATE_CONTRACTOR_AGREEMENT,
            {
              method: "POST",
              body: {
                agreementData,
                userEmail,
                userFirstName,
                userLastName,
              },
              skipAuth: true,
            },
          );

        if (response.status === "success" && response.agreement_id) {
          toast({
            title: "Success!",
            description:
              "Your contractor agreement has been generated and sent to your email. Please check your inbox.",
          });

          form.reset(); // Clear the form on success
          if (onSuccess) {
            onSuccess();
          }
        } else {
          throw new Error(
            response.error || "Failed to generate contractor agreement",
          );
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to generate contractor agreement",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const formContent = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <Alert className="mb-4 bg-white border-primary/20">
          <Info className="h-4 w-4 text-primary" />
          <AlertTitle>Contractor Agreement Generator</AlertTitle>
          <AlertDescription>
            This tool helps you quickly generate a professional contractor
            agreement. Please fill in all required fields accurately. The
            generated document will be sent to the email address you provide.
          </AlertDescription>
        </Alert>

        {/* Parties Information */}
        <Card>
          <CardHeader className="bg-muted/50">
            <CardTitle>Parties Information</CardTitle>
            <CardDescription>
              Enter details about the client and contractor.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="client_full_legal_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Full Legal Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., Jane Smith or ABC Ltd"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="client_address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Address</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., 123 Client St, London"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contractor_full_legal_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contractor Full Legal Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., John Doe" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contractor_address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contractor Address</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., 456 Contractor Ave, London"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contractor_business_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contractor Business Name (Optional)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., John Doe Services" />
                    </FormControl>
                    <FormDescription>
                      If the contractor operates under a business name, enter it
                      here.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card>
          <CardHeader className="bg-muted/50">
            <CardTitle>Services Description</CardTitle>
            <CardDescription>
              Define the services to be provided by the contractor.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <FormField
              control={form.control}
              name="schedule_a_services_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description of Services</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Describe in detail the services the contractor will provide..."
                      className="min-h-[100px]"
                    />
                  </FormControl>
                  <FormDescription>
                    Be specific about the contractor's responsibilities,
                    deliverables, and expected outcomes.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Fees & Invoicing */}
        <Card>
          <CardHeader className="bg-muted/50">
            <CardTitle>Fees & Invoicing</CardTitle>
            <CardDescription>
              Specify payment details and invoicing arrangements.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="grid grid-cols-3 gap-2 items-end">
                <FormField
                  control={form.control}
                  name="fee_currency_symbol"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="£">£ (GBP)</SelectItem>
                          <SelectItem value="$">$ (USD)</SelectItem>
                          <SelectItem value="€">€ (EUR)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="fee_rate"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>Fee Rate</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value) || 0)
                          }
                          placeholder="e.g., 50"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="fee_basis"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fee Basis</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select fee basis" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="hour">Per Hour</SelectItem>
                        <SelectItem value="day">Per Day</SelectItem>
                        <SelectItem value="week">Per Week</SelectItem>
                        <SelectItem value="month">Per Month</SelectItem>
                        <SelectItem value="project">Per Project</SelectItem>
                        <SelectItem value="milestone">Per Milestone</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="invoice_frequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Frequency</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select invoice frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="fortnightly">Fortnightly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="milestone">
                          After Each Milestone
                        </SelectItem>
                        <SelectItem value="completion">
                          Upon Completion
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="payment_due_days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Due (Days)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value, 10) || 0)
                        }
                        placeholder="e.g., 30"
                      />
                    </FormControl>
                    <FormDescription>
                      Number of days after invoice receipt when payment is due.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="md:col-span-2">
                <FormField
                  control={form.control}
                  name="schedule_b_fees_description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Additional Fee Details (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Describe any additional fee arrangements, expenses, or payment terms..."
                          className="min-h-[80px]"
                        />
                      </FormControl>
                      <FormDescription>
                        Include details on expenses reimbursement, late payment
                        fees, etc.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Safeguarding & Data Protection */}
        <Card>
          <CardHeader className="bg-muted/50">
            <CardTitle>Safeguarding & Data Protection</CardTitle>
            <CardDescription>
              Specify background check requirements and data protection
              regulations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-1 gap-6">
              <FormField
                control={form.control}
                name="background_check_examples"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Required Background Checks</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., DBS certificate, references"
                      />
                    </FormControl>
                    <FormDescription>
                      List the background checks or clearances required.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="schedule_c_safeguarding_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Additional Safeguarding Requirements (Optional)
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Describe any additional safeguarding policies or requirements..."
                        className="min-h-[80px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="data_protection_regulation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data Protection Regulation</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select data protection regulation" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="UK GDPR">UK GDPR</SelectItem>
                        <SelectItem value="EU GDPR">EU GDPR</SelectItem>
                        <SelectItem value="CCPA">CCPA (California)</SelectItem>
                        <SelectItem value="PIPEDA">PIPEDA (Canada)</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Insurance & Liability */}
        <Card>
          <CardHeader className="bg-muted/50">
            <CardTitle>Insurance & Liability</CardTitle>
            <CardDescription>
              Specify insurance requirements and liability limitations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <FormField
              control={form.control}
              name="has_insurance"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Contractor Must Have Insurance</FormLabel>
                    <FormDescription>
                      Check this if the contractor must maintain professional
                      insurance.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {form.watch("has_insurance") && (
              <div className="border rounded-md p-4">
                <FormLabel className="mb-3 block">
                  Insurance Cover Amount
                </FormLabel>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <FormField
                    control={form.control}
                    name="insurance_currency_symbol"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="£">£ (GBP)</SelectItem>
                            <SelectItem value="$">$ (USD)</SelectItem>
                            <SelectItem value="€">€ (EUR)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="insurance_cover_amount"
                    render={({ field }) => (
                      <FormItem className="md:col-span-3">
                        <FormLabel>Amount</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value) || 0)
                            }
                            placeholder="e.g., 1000000"
                          />
                        </FormControl>
                        <FormDescription>
                          Minimum coverage required.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            <div className="border rounded-md p-4">
              <FormLabel className="mb-3 block">Liability Cap Amount</FormLabel>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="liability_cap_currency_symbol"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="£">£ (GBP)</SelectItem>
                          <SelectItem value="$">$ (USD)</SelectItem>
                          <SelectItem value="€">€ (EUR)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="liability_cap_amount"
                  render={({ field }) => (
                    <FormItem className="md:col-span-3">
                      <FormLabel>Amount</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value) || 0)
                          }
                          placeholder="e.g., 10000"
                        />
                      </FormControl>
                      <FormDescription>
                        Maximum liability amount (optional).
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Term & Termination */}
        <Card>
          <CardHeader className="bg-muted/50">
            <CardTitle>Term & Termination</CardTitle>
            <CardDescription>
              Specify the agreement duration and termination terms.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !field.value && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? (
                              format(
                                new Date(field.value + "T00:00:00"),
                                "dd/MM/yyyy",
                              )
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[100]">
                        <Calendar
                          mode="single"
                          selected={
                            field.value
                              ? new Date(field.value + "T00:00:00")
                              : undefined
                          }
                          onSelect={(date) =>
                            field.onChange(
                              date ? format(date, "yyyy-MM-dd") : "",
                            )
                          }
                          classNames={{
                            nav_button: "h-7 w-7 bg-transparent p-0 text-gray-700 hover:text-gray-900",
                            caption_label: "text-sm font-medium text-gray-900",
                            day_outside: "day-outside text-gray-600 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                            day_disabled: "text-gray-400 opacity-50 cursor-not-allowed",
                            day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-gray-900 hover:bg-accent hover:text-accent-foreground"
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date_or_completion"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date or Completion Criteria</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., 2023-12-31 or On project completion"
                      />
                    </FormControl>
                    <FormDescription>
                      Enter a specific end date or describe completion criteria.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="termination_notice_days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Termination Notice Period (Days)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value, 10) || 0)
                        }
                        placeholder="e.g., 30"
                      />
                    </FormControl>
                    <FormDescription>
                      Number of days' notice required to terminate the
                      agreement.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Non-Solicitation */}
        <Card>
          <CardHeader className="bg-muted/50">
            <CardTitle>Non-Solicitation</CardTitle>
            <CardDescription>
              Optional clause to prevent contractor from soliciting client's
              employees or customers.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <FormField
              control={form.control}
              name="non_solicitation_clause"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Include Non-Solicitation Clause</FormLabel>
                    <FormDescription>
                      Prevents the contractor from soliciting your employees,
                      clients, or customers.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {form.watch("non_solicitation_clause") && (
              <FormField
                control={form.control}
                name="non_solicitation_months"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Non-Solicitation Period (Months)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value, 10) || 0)
                        }
                        placeholder="e.g., 6"
                      />
                    </FormControl>
                    <FormDescription>
                      Number of months the restriction applies after contract
                      ends.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </CardContent>
        </Card>

        {/* Governing Law & Dispute Resolution */}
        <Card>
          <CardHeader className="bg-muted/50">
            <CardTitle>Governing Law & Dispute Resolution</CardTitle>
            <CardDescription>
              Specify the governing law and dispute resolution procedures.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="governing_law_country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Governing Law Country</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="United Kingdom">
                          United Kingdom
                        </SelectItem>
                        <SelectItem value="England and Wales">
                          England and Wales
                        </SelectItem>
                        <SelectItem value="Scotland">Scotland</SelectItem>
                        <SelectItem value="Northern Ireland">
                          Northern Ireland
                        </SelectItem>
                        <SelectItem value="United States">
                          United States
                        </SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mediation_rules"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mediation Rules</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select mediation rules" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="CEDR">
                          CEDR (Centre for Effective Dispute Resolution)
                        </SelectItem>
                        <SelectItem value="AAA">
                          AAA (American Arbitration Association)
                        </SelectItem>
                        <SelectItem value="ICC">
                          ICC (International Chamber of Commerce)
                        </SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* User Contact Information */}
        {!embedded && (
          <Card>
            <CardHeader className="bg-muted/50">
              <CardTitle>Your Contact Information</CardTitle>
              <CardDescription>
                Please provide your details to receive the generated agreement.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="userFirstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your First Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Alex" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="userLastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Last Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Taylor" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="userEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Email Address</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        {...field}
                        placeholder="e.g., alex.taylor@example.com"
                      />
                    </FormControl>
                    <FormDescription>
                      The generated agreement will be sent here.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="border rounded-md p-4 bg-muted/50">
                <div className="flex flex-col md:flex-row items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground">
                      Need help with your contractor agreement?
                    </p>
                    <p>
                      Hirelo offers expert guidance on contractor relationships
                      and tax compliance.
                    </p>
                  </div>
                  <Button
                    className="whitespace-nowrap"
                    onClick={() =>
                      window.open("https://www.hirelo.io/contact", "_blank")
                    }
                  >
                    Contact Support
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => form.reset()}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Clear Form
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? "Generating..." : "Generate Contractor Agreement"}
          </Button>
        </div>
      </form>
    </Form>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-background">
      <PublicNavBar />

      <div className="w-full max-w-4xl space-y-6 px-4 py-8 md:py-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white rounded-lg p-6 shadow-lg">
          <div className="text-center md:text-left">
            <h1 className="text-3xl text-foreground">
              Independent Contractor Agreement
            </h1>
            <p className="text-muted-foreground mt-2">
              Fill in the details below to generate an independent contractor
              agreement.
            </p>
            <Button
              className="mt-4"
              onClick={() =>
                window.open("https://www.hirelo.io/demo", "_blank")
              }
            >
              Try Demo
            </Button>
          </div>
          <div className="w-full md:w-1/2 relative">
            <img
              src={wholeFamily}
              alt="Family with child"
              className="rounded-lg object-cover shadow-md w-full max-h-64 md:max-h-none"
            />
          </div>
        </div>

        {formContent}

        <div className="mt-8">
          <PublicFooter />
        </div>
      </div>
    </div>
  );
}
